import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  Building2,
  Compass,
  GitBranch,
  Globe,
  Landmark,
  Layers,
  LineChart as LineChartIcon,
  UserRound,
  Users,
} from 'lucide-react'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  CHART_MAP_GRID_OVERLAY,
  CHART_MAP_RADIAL_OVERLAY,
  CHART_TOOLTIP_PROPS,
  chartAxisProps,
  chartGridProps,
  chartPartOfWholeColor,
  chartSemanticProgressColor,
  chartSeriesColor,
} from '@/config/chartTheme'
import {
  AlertRail,
  AttentionTable,
  BookUniverseLine,
  DashboardGrid,
  DashboardShell,
  DashboardTabs,
  FigureTable,
  FigureValue,
  GridCell,
  KpiStrip,
  KpiTile,
  MovementRail,
  Panel,
  QuickActions,
  ScorecardPanel,
  displayFigure,
  figureToneClass,
  formatFigure,
  scorecardMetrics,
  useDashboardView,
  useDrillDown,
} from '@/components/dashboards'
import UnmeasuredLine, { BasisNote, splitFigures } from '@/components/dashboards/KnownFigures'
import CohortBar from '@/components/analytics/explorer/CohortBar'
import FlowChart from '@/components/analytics/explorer/FlowChart'
import FunnelStrip from '@/components/analytics/explorer/FunnelStrip'
import PipelineExplorer from '@/components/analytics/explorer/PipelineExplorer'
import { useDealFacts } from '@/components/analytics/explorer/analyticsQueries'
import { useCohort } from '@/components/analytics/explorer/explorerState'
import Leaderboard from '@/components/analytics/people/Leaderboard'
import TeamWorkloadPanel from '@/components/analytics/people/TeamWorkload'
import {
  describeFilters,
  drillForIds,
  type FlowResponse,
  type FunnelResponse,
  type PivotRow,
} from '@/services/dashboardAnalytics'
import type { DashboardTab } from '@/components/dashboards'
import type {
  AlertItem,
  AlertSeverity,
  AttentionPanelData,
  BookUniverse,
  DashboardPanelData,
  DrillDescriptor,
  Figure,
  FigureDelta,
  FigureTableColumn,
  FigureTableRow,
  FigureTone,
  MovementItem,
  ScorecardPanelData,
} from '@/types/dashboards'
import CityConcentrationMap, {
  type CityConcentrationData,
} from '@/components/dashboards/CityConcentrationMap'
import type { TeamRevenueData } from '@/components/dashboards/TeamRevenueState'
import { concentrationFooter, mapFootnote, outlookKicker } from './ceoBoardCopy'
import {
  AgingWatch,
  CounterpartyBoard,
  LeverageProfile,
  MaturityLadder,
  unmappedIds,
  useCeoBoard,
} from './ceoAnalyticsPanels'

/* ── Wire shapes ────────────────────────────────────────────────────────────
 * The CEO board's panels are board-specific documents rather than the generic
 * kit unions in `types/dashboards.ts`, so they are declared against what
 * `services/dashboards/executive.py::build_board` actually emits. Anything the
 * service does not ship is absent here too — it is not filled in locally.
 */

interface TilesPanel {
  tiles: Figure[]
}

/** Forecast year-end, from the forecast months already on record.
 *
 *  The reference layout asked for committed / base / upside / at-risk tiles and
 *  `BusinessUnitPeriod` carries no scenario dimension at all, so the service
 *  ships the recorded periods summed and states that the split does not exist.
 *  `scenarioSplit` stays null until something records one. */
interface OutlookPanel {
  periodYear: number
  /** Finance-closed months this year. */
  closedMonths: number
  /** Months with a loan funded, when production is the live book's; null
   *  when a finance close governs. */
  fundedMonths?: number | null
  forecastMonths: number
  tiles: Figure[]
  scenarioSplit: null
  basis: string
}

interface ScorecardPayload {
  metrics: Figure[]
  overall: Figure
  /** How much of the scorecard the overall ring actually averages.
   *
   *  A 0% overall carries very different weight when five metrics scored it and
   *  when one did — and on a partition that has only started to fill, four of
   *  the five are routinely unmeasurable. Optional: a board built before the
   *  basis line landed ships no footnote. */
  footnote?: string
}

interface UnitRow {
  unitId: string | number
  code: string
  name: string
  kind: string
  leader: string | null
  production: Figure
  planProduction: Figure
  productionVsPlan: Figure
  netRevenue: Figure
  revenueVsPlan: Figure
  exposure: Figure
  watchlist: Figure
  roePct: Figure
}

interface UnitsPanel {
  rows: UnitRow[]
}

interface TrendPoint {
  month: string
  label: string
  production: number | null
  planProduction: number | null
  netRevenue: number | null
  planRevenue: number | null
}

interface PerformanceTrendPanel {
  points: TrendPoint[]
  momProduction: FigureDelta | null
}

interface CapitalPanel {
  periodMonth: string | null
  tiles: Figure[]
}

interface ConcentrationRow {
  dimension: string
  value: string
  limitPct: number | null
  warningPct: number | null
  actualPct: Figure
  actualAmount: Figure
  headroomPts: number | null
}

interface ConcentrationPanel {
  rows: ConcentrationRow[]
  /** Rows measured against a configured policy limit. 0 means every share is
   *  the observed book mix and nothing can be "within" or "over" a limit. */
  limitsConfigured?: number
  /** What the shares are of, when that differs from "Total exposure". */
  basis?: string
}

interface CreditPoint {
  month: string | null
  label: string
  current: number | null
  watchlist: number | null
  criticized: number | null
  dpd90: number | null
  expectedLoss: number | null
}

interface CreditPanel {
  points: CreditPoint[]
  tiles: Figure[]
}

interface RelationshipRow {
  id: number
  clientName: string
  health: string
  isTop50: boolean
  isRepeat: boolean
  ytdProduction: number | null
  dealCount: number | null
  crossSellRevenue: number | null
  walletSharePct: number | null
  drill: DrillDescriptor | null
}

interface RelationshipsPanel {
  summary: Figure[]
  repeatRate: Figure
  rows: RelationshipRow[]
}

interface MarketIndicator {
  indicator: string
  value: number | null
  label: string | null
  trend: string | null
  asOf: string | null
  commentary: string | null
}

interface PostureNote {
  indicator: string
  commentary: string | null
  trend: string | null
}

interface MarketPanel {
  indicators: MarketIndicator[]
  postureNotes: PostureNote[]
}

interface MaturityBucket {
  key: string
  label: string
  count: Figure
  balance: Figure
}

interface MaturitiesPanel {
  buckets: MaturityBucket[]
  /** Funded loans whose closing + initial term is already behind them. */
  pastInitialMaturity?: Figure
  pastInitialMaturityBalance?: Figure
  /** Of those past initial maturity, the ones still inside the stated extension. */
  pastInsideExtension?: Figure
  maturityNotRecorded?: Figure
  /** Funded loans the wall ladders. */
  fundedLoans?: number
  /** What the ladder is computed from, in one line. */
  basis?: string
}

interface AttentionRow {
  source: string
  title: string
  detail: string | null
  amount: number | null
  daysToDeadline: number | null
  severity: string | null
  action: string
  score: number
  drill: DrillDescriptor | null
  href: string | null
}

interface AttentionPanel {
  rows: AttentionRow[]
}

/* The four rail panels below carry the same wire shapes as the Legal board's,
 * because `executive.build_board` deliberately emits the same panel keys so both
 * pages reuse one set of kit components. */

interface HealthPanel {
  rails: Figure[]
}

interface CriticalAlertsPanel {
  alerts: Figure[]
}

interface MovementWireRow {
  source: string
  title: string
  change: string | null
  at: string | null
  actor: string | null
  drill?: DrillDescriptor | null
  href?: string | null
}

interface MovementWirePanel {
  rows: MovementWireRow[]
}

interface QuickActionWireRow {
  label: string
  count: number | null
  drill?: DrillDescriptor | null
}

interface QuickActionsWirePanel {
  actions: QuickActionWireRow[]
}

interface LeaderboardSlice {
  dimension: string
  label: string
  rows: PivotRow[]
  notRecorded?: { count: number; dealIds: string[] }
}

interface LeaderboardsPanel {
  underwriter?: LeaderboardSlice
  responsibleParty?: LeaderboardSlice
  brokerage?: LeaderboardSlice
  basis?: string
}

interface TeamPanel {
  people: Array<{ name: string; count: number; onRoster?: boolean }>
  basis?: string
  unattributed?: { count: number; dealIds: string[] }
}

/** Year-to-date actual, forecast remaining, and the two summed.
 *
 *  Deliberately NOT the four scenario tiles of the reference layout: the data
 *  model has no scenario dimension, and splitting one forecast number four ways
 *  would invent the taxonomy rather than report it. The panel footer says so.
 */
function OutlookTiles({ data }: { data: OutlookPanel }) {
  const { known } = splitFigures(data.tiles)
  if (known.length === 0) return null
  return (
    <div className="mb-2 grid flex-shrink-0 grid-cols-3 gap-x-3 gap-y-2">
      {known.map((tile) => (
        <div key={tile.label} className="min-w-0">
          <div className="terminal-kicker !text-[7.5px] break-words" title={tile.label}>
            {tile.label}
          </div>
          <div className="font-mono text-[11.5px] tabular-nums text-stone-200">
            <FigureValue figure={tile} />
          </div>
        </div>
      ))}
    </div>
  )
}

/** `getPanel` is typed against the generic kit unions; these panels are board
 *  shapes, so they are read with the same absent-key tolerance and cast here. */
function readPanel<T>(panels: Record<string, DashboardPanelData>, key: string): T | null {
  const panel = panels[key]
  return panel ? (panel as unknown as T) : null
}

/** Envelope for a raw wire number so it renders through the same formatter as
 *  every server-built Figure — null still reads '--', never 0. */
function wrap(
  label: string,
  value: number | string | null,
  format: Figure['format'],
  extra?: Partial<Figure>,
): Figure {
  return { label, value, format, ...extra }
}

function numeric(figure: Figure | undefined): number | null {
  return typeof figure?.value === 'number' ? figure.value : null
}

function titleCase(value: string): string {
  return value.replace(/_/g, ' ')
}

/* ── Vocabulary → presentation ──────────────────────────────────────────────
 * Keys are the model vocabularies from the design spec §5.5, not data.
 */

const SEVERITY_TONE: Record<string, FigureTone> = {
  high: 'negative',
  medium: 'warning',
  low: 'neutral',
}

const TREND_ICON: Record<string, typeof ArrowUp> = {
  improving: ArrowUp,
  stable: ArrowRight,
  deteriorating: ArrowDown,
  negative: ArrowDown,
  weak: ArrowDown,
}

const TREND_CLASS: Record<string, string> = {
  improving: 'text-emerald-400',
  stable: 'text-stone-400',
  deteriorating: 'text-red-400',
  negative: 'text-red-400',
  weak: 'text-red-400',
}

/** The alert rail's severity comes from the server's own tone on the figure —
 *  the page does not re-band a number the service already judged. */
function alertSeverity(tone?: FigureTone): AlertSeverity {
  if (tone === 'negative') return 'critical'
  if (tone === 'warning') return 'high'
  return 'low'
}

function dueLabel(days: number | null): string | undefined {
  if (days == null) return undefined
  if (days < 0) return 'Overdue'
  if (days < 1) return 'Today'
  return `${Math.round(days)}d`
}

/* ── Local panel bodies ─────────────────────────────────────────────────────
 * Built here rather than in the shared kit: each is specific to the CEO
 * board's panel shapes and has no second caller.
 */

/** Full-width plan attainment bar under the KPI strip. Reads the scorecard's
 *  own metrics — production first, revenue second, the order
 *  `executive.scorecard()` emits — so the bar and the scorecard cannot
 *  disagree about the same percentage. */
function PlanProgress({ production, revenue }: { production?: Figure; revenue?: Figure }) {
  const value = numeric(production)
  const clamped = value == null ? 0 : Math.max(0, Math.min(100, value))

  return (
    <div className="surface-panel flex flex-shrink-0 items-center gap-3 rounded-md px-3 py-2">
      <div className="w-[150px] flex-shrink-0">
        <div className="terminal-kicker !text-[8px]">{production?.label ?? 'Plan attainment'}</div>
        <div className="mt-0.5 text-[15px] font-semibold leading-none text-stone-100">
          {production ? <FigureValue figure={production} /> : <span className="text-stone-600">--</span>}
        </div>
      </div>

      <div className="chart-viz-track relative h-2 min-w-0 flex-1 rounded-full">
        {value != null && (
          <div
            className="chart-viz-bar chart-viz-bar--rounded h-full"
            style={{ width: `${clamped}%`, background: chartSemanticProgressColor(clamped) }}
          />
        )}
      </div>

      <div className="w-[190px] flex-shrink-0 text-right">
        <div className="terminal-kicker !text-[8px]">{revenue?.label ?? 'Revenue vs plan'}</div>
        <div className="mt-0.5 font-mono text-[12px] tabular-nums text-stone-200">
          {revenue ? <FigureValue figure={revenue} align="right" /> : <span className="text-stone-600">--</span>}
        </div>
      </div>
    </div>
  )
}

/** Region cards over the platform's map backdrop. The backdrop is chrome from
 *  chartTheme — no geography is drawn from the figures. */
function FranchiseCards({ units }: { units: UnitRow[] }) {
  // A figure no unit carries (ROE before a finance close) is a column of
  // dashes; it is stated once in the panel footer instead.
  const shownKeys = (['exposure', 'production', 'roePct'] as const).filter((key) =>
    units.some((unit) => numeric(unit[key]) != null),
  )
  const anyLeader = units.some((unit) => unit.leader)
  const anyPlan = units.some((unit) => numeric(unit.productionVsPlan) != null)
  return (
    <div className="flex min-h-0 flex-col gap-2">
      <div
        className="relative flex-1 overflow-hidden rounded-md border border-amber-400/[0.08] p-2"
        style={{ backgroundImage: CHART_MAP_RADIAL_OVERLAY }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{ backgroundImage: CHART_MAP_GRID_OVERLAY, backgroundSize: '26px 26px' }}
        />
        <div className="relative space-y-1.5">
          {units.map((unit, index) => (
            <div
              key={unit.unitId}
              className="surface-panel rounded-md px-2.5 py-1.5"
              style={{ borderLeft: `2px solid ${chartSeriesColor(index)}` }}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="truncate text-[11.5px] font-medium text-stone-100" title={unit.name}>
                  {unit.name}
                </span>
                <span className="flex-shrink-0 font-mono text-[9px] uppercase tracking-widest text-stone-600">
                  {unit.code}
                </span>
              </div>
              <div className="mt-1 grid gap-1.5" style={{ gridTemplateColumns: `repeat(${Math.max(1, shownKeys.length)}, minmax(0, 1fr))` }}>
                {shownKeys.map((key) => unit[key]).map((figure, i) => (
                  <div key={i} className="min-w-0">
                    <div className="terminal-kicker !text-[7.5px] truncate">{figure.label}</div>
                    <div className="font-mono text-[11px] tabular-nums text-stone-200">
                      <FigureValue figure={figure} />
                    </div>
                  </div>
                ))}
              </div>
              {(anyLeader || anyPlan) && (
                <div className="mt-0.5 flex items-center gap-1 text-[10px] text-stone-500">
                  {anyLeader && <span className="truncate">{unit.leader ?? '--'}</span>}
                  {anyPlan && (
                    <span className="ml-auto flex-shrink-0 font-mono tabular-nums">
                      <FigureValue figure={unit.productionVsPlan} align="right" />
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-shrink-0 flex-wrap items-center gap-x-3 gap-y-1">
        {units.map((unit, index) => (
          <span key={unit.unitId} className="flex items-center gap-1 text-[9px] text-stone-500">
            <span
              className="h-1.5 w-3 rounded-sm"
              style={{ background: chartSeriesColor(index) }}
              aria-hidden="true"
            />
            {unit.name}
          </span>
        ))}
      </div>
    </div>
  )
}

function amountTick(value: number): string {
  return formatFigure({ label: '', value, format: 'currencyM' })
}

/** Monthly production against plan with the net-revenue overlay.
 *
 *  Closed months only. The forecast months are summed into the tiles above
 *  rather than drawn here: plotting a forecast on the same axis as an actual,
 *  in the same colour, is how a projection gets read as a result.
 */
function ProductionOutlook({ points }: { points: TrendPoint[] }) {
  const hasPlan = points.some((point) => point.planProduction != null)
  const hasRevenue = points.some((point) => point.netRevenue != null)
  const withCumulative = useMemo(() => {
    let running: number | null = null
    return points.map((point) => {
      if (point.production != null) running = (running ?? 0) + point.production
      return { ...point, cumulativeProduction: running }
    })
  }, [points])
  return (
    // `flex-1` on the root, not only on the chart wrapper. Without it this block
    // sizes to its content inside the panel's flex column, the wrapper's own
    // flex-1 has nothing to fill, and ResponsiveContainer resolves to zero
    // height — a chart that renders no pixels and raises nothing.
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="min-h-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={withCumulative} margin={{ top: 6, right: 4, left: -8, bottom: 0 }}>
            <CartesianGrid {...chartGridProps()} />
            <XAxis dataKey="label" {...chartAxisProps(9)} interval="preserveStartEnd" />
            <YAxis yAxisId="month" {...chartAxisProps(9)} tickFormatter={amountTick} width={52} />
            <YAxis yAxisId="ytd" orientation="right" {...chartAxisProps(9)} tickFormatter={amountTick} width={52} />
            <Tooltip
              {...CHART_TOOLTIP_PROPS}
              formatter={(value: number, name: string) => [amountTick(value), titleCase(name)]}
            />
            <Bar yAxisId="month" dataKey="production" name="Funded in month" fill={chartSeriesColor(3)} radius={[3, 3, 0, 0]} />
            <Line
              yAxisId="ytd"
              type="monotone"
              dataKey="cumulativeProduction"
              name="Cumulative"
              stroke="#F6DF9A"
              strokeWidth={1.6}
              dot={false}
              connectNulls
            />
            {hasPlan && (
              <Line
                yAxisId="month"
                type="monotone"
                dataKey="planProduction"
                name="Plan"
                stroke={chartSeriesColor(1)}
                strokeWidth={1.5}
                strokeDasharray="4 3"
                dot={false}
              />
            )}
            {hasRevenue && (
              <Line
                yAxisId="month"
                type="monotone"
                dataKey="netRevenue"
                name="Net revenue"
                stroke={chartSeriesColor(7)}
                strokeWidth={2}
                dot={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

/** One dimension of the concentration panel — exposure bar, share of book and
 *  the policy limit marker, scaled against the widest row in the group. */
function ConcentrationList({ label, rows }: { label: string; rows: ConcentrationRow[] }) {
  const scale = Math.max(
    1,
    ...rows.map((row) => Math.max(numeric(row.actualPct) ?? 0, row.limitPct ?? 0) * 1.15),
  )

  return (
    <div className="space-y-1.5">
      <div className="terminal-kicker !text-[8px]">{label}</div>
      {rows.map((row) => {
        const actual = numeric(row.actualPct)
        return (
          <div key={`${row.dimension}-${row.value}`} className="space-y-0.5">
            <div className="flex items-baseline justify-between gap-2 text-[11px]">
              <span className="min-w-0 truncate text-stone-300" title={row.value}>
                {row.value}
              </span>
              <span className="flex-shrink-0 font-mono tabular-nums text-stone-400">
                <FigureValue figure={row.actualAmount} align="right" />
              </span>
              <span className="w-14 flex-shrink-0 text-right font-mono tabular-nums">
                <FigureValue figure={row.actualPct} align="right" />
              </span>
            </div>
            <div className="chart-viz-track relative h-1.5 w-full rounded-full">
              {actual != null && (
                <div
                  className="chart-viz-bar chart-viz-bar--rounded h-full"
                  style={{
                    width: `${Math.min(100, (actual / scale) * 100)}%`,
                    background:
                      row.actualPct.tone === 'negative'
                        ? chartSeriesColor(0)
                        : row.actualPct.tone === 'warning'
                          ? chartSeriesColor(3)
                          : chartSeriesColor(7),
                  }}
                />
              )}
              {row.limitPct != null && (
                <div
                  className="absolute top-[-2px] h-[10px] w-px bg-stone-300/70"
                  style={{ left: `${Math.min(100, (row.limitPct / scale) * 100)}%` }}
                  title={`Limit ${row.limitPct}%`}
                />
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/** Maturity ladder. Each column carries the bucket's own drill descriptor, so
 *  a bar opens the loans behind it exactly like the count beside it. */
function MaturityWall({ buckets }: { buckets: MaturityBucket[] }) {
  const { open } = useDrillDown()
  const scale = Math.max(0, ...buckets.map((bucket) => numeric(bucket.balance) ?? 0))

  return (
    <div className="flex flex-shrink-0 flex-col gap-2">
      <div className="flex h-[120px] flex-shrink-0 items-end gap-2">
        {buckets.map((bucket, index) => {
          const balance = numeric(bucket.balance)
          const height = balance != null && scale > 0 ? Math.max(2, (balance / scale) * 100) : 0
          const drill = bucket.count.drill
          return (
            <button
              key={bucket.key}
              type="button"
              onClick={() => open(drill)}
              disabled={!drill}
              title={drill ? `${drill.title} — open records` : undefined}
              className="flex h-full min-w-0 flex-1 flex-col justify-end rounded-sm transition-colors hover:bg-amber-400/[0.04] focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/50 disabled:cursor-default disabled:hover:bg-transparent"
            >
              <span className="mb-1 truncate text-center font-mono text-[9px] tabular-nums text-stone-400">
                {displayFigure(bucket.balance)}
              </span>
              <span
                className="chart-viz-bar chart-viz-bar--rounded-sm w-full"
                style={{
                  height: `${height}%`,
                  background: chartPartOfWholeColor(index, buckets.length),
                }}
              />
            </button>
          )
        })}
      </div>

      <div className="flex flex-shrink-0 gap-2">
        {buckets.map((bucket) => (
          <div key={bucket.key} className="min-w-0 flex-1 text-center">
            <div className="truncate font-mono text-[9px] uppercase tracking-wider text-stone-500">
              {bucket.label}
            </div>
            <div className="font-mono text-[11px] tabular-nums text-stone-200">
              <FigureValue figure={bucket.count} align="center" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/** True when the board's maturity buckets carry at least one dated loan. */
function hasMaturities(buckets: MaturityBucket[]): boolean {
  return buckets.some((bucket) => (numeric(bucket.count) ?? 0) > 0 || (numeric(bucket.balance) ?? 0) > 0)
}

function StatGrid({ figures, columns = 2 }: { figures: Figure[]; columns?: number }) {
  return (
    <div
      className="grid gap-1.5"
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {figures.map((figure) => (
        <div key={figure.label} className="min-w-0 border-b border-amber-400/[0.06] pb-1">
          <div className="terminal-kicker !text-[7.5px] break-words" title={figure.label}>
            {figure.label}
          </div>
          <div className="font-mono text-[12px] tabular-nums text-stone-100">
            <FigureValue figure={figure} showDelta />
          </div>
          {figure.basis && <div className="text-[9px] leading-snug text-stone-500">{figure.basis}</div>}
        </div>
      ))}
    </div>
  )
}

/** The enterprise-health rail: four 0-100 dimensions, each from a different
 *  table. A dimension with nothing to score draws no bar rather than a zero one. */
function RailBars({ figures }: { figures: Figure[] }) {
  return (
    <div className="space-y-2">
      {figures.map((figure) => {
        const score = numeric(figure)
        const clamped = score == null ? null : Math.max(0, Math.min(100, score))
        return (
          <div key={figure.label} className="space-y-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-[11px] text-stone-300" title={figure.label}>
                {figure.label}
              </span>
              <span
                className={clsx(
                  'flex-shrink-0 font-mono text-[11px] tabular-nums',
                  figureToneClass(figure.tone),
                )}
              >
                <FigureValue figure={figure} align="right" />
              </span>
            </div>
            <div className="chart-viz-track h-1.5 w-full rounded-full">
              {clamped != null && (
                <div
                  className="chart-viz-bar chart-viz-bar--rounded h-full"
                  style={{
                    width: `${clamped}%`,
                    background: chartSemanticProgressColor(clamped),
                  }}
                />
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function TrendGlyph({ trend }: { trend: string | null }) {
  if (!trend) return <span className="text-stone-600">--</span>
  const Icon = TREND_ICON[trend] ?? ArrowRight
  return (
    <span className={clsx('inline-flex items-center gap-1 text-[10px]', TREND_CLASS[trend] ?? 'text-stone-400')}>
      <Icon size={10} />
      {titleCase(trend)}
    </span>
  )
}

const TABS: DashboardTab[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'explorer', label: 'Pipeline Explorer', icon: Compass },
  { id: 'production', label: 'Production & Earnings' },
  { id: 'risk', label: 'Risk & Capital' },
  { id: 'relationships', label: 'Relationships & Markets' },
  { id: 'geography-team', label: 'Geography & Team' },
]

/** Literal classes so Tailwind keeps them: one column per measured tile. */
const STRIP_COLUMNS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-2 md:grid-cols-4',
  5: 'grid-cols-3 xl:grid-cols-5',
  6: 'grid-cols-3 xl:grid-cols-6',
  7: 'grid-cols-4 xl:grid-cols-7',
  8: 'grid-cols-4 xl:grid-cols-8',
  9: 'grid-cols-3 xl:grid-cols-9',
}

export default function CeoDashboard() {
  const [cohort, setCohort] = useCohort()
  const board = useCeoBoard(cohort)
  const { panels, isLoading, error } = board
  const retry = () => void board.refetch()
  const cohortLabel = describeFilters(cohort)
  const cohortFacts = useDealFacts(cohort)

  const headline = readPanel<TilesPanel>(panels, 'headline')
  const scorecard = readPanel<ScorecardPayload>(panels, 'scorecard')
  const units = readPanel<UnitsPanel>(panels, 'units')
  const trend = readPanel<PerformanceTrendPanel>(panels, 'performanceTrend')
  const capital = readPanel<CapitalPanel>(panels, 'capital')
  const outlook = readPanel<OutlookPanel>(panels, 'outlook')
  const concentration = readPanel<ConcentrationPanel>(panels, 'concentration')
  const credit = readPanel<CreditPanel>(panels, 'credit')
  const relationships = readPanel<RelationshipsPanel>(panels, 'relationships')
  const market = readPanel<MarketPanel>(panels, 'market')
  const maturities = readPanel<MaturitiesPanel>(panels, 'maturities')
  const attention = readPanel<AttentionPanel>(panels, 'attention')
  const health = readPanel<HealthPanel>(panels, 'health')
  const criticalAlerts = readPanel<CriticalAlertsPanel>(panels, 'criticalAlerts')
  const movement = readPanel<MovementWirePanel>(panels, 'recentMovement')
  const quickActions = readPanel<QuickActionsWirePanel>(panels, 'quickActions')
  const cityConcentration = readPanel<CityConcentrationData>(panels, 'cityConcentration')
  const teamRevenue = readPanel<TeamRevenueData>(panels, 'teamRevenue')
  const universe = readPanel<BookUniverse>(panels, 'universe')
  // Book-depth panels (deal facts, cut to the cohort with the rest of the
  // board): the same documents /analytics/funnel, /flow and /pivot return.
  const pipelineFunnel = readPanel<FunnelResponse>(panels, 'pipelineFunnel')
  const monthlyFlow = readPanel<FlowResponse>(panels, 'monthlyFlow')
  const leaderboards = readPanel<LeaderboardsPanel>(panels, 'leaderboards')
  const team = readPanel<TeamPanel>(panels, 'team')

  const regions = useMemo(
    () => (units?.rows ?? []).filter((row) => row.kind === 'region'),
    [units],
  )

  const headlineSplit = useMemo(() => splitFigures(headline?.tiles ?? []), [headline])
  const scorecardSplit = useMemo(() => splitFigures(scorecard?.metrics ?? []), [scorecard])
  const healthSplit = useMemo(() => splitFigures(health?.rails ?? []), [health])
  const alertSplit = useMemo(() => splitFigures(criticalAlerts?.alerts ?? []), [criticalAlerts])
  const creditSplit = useMemo(() => splitFigures(credit?.tiles ?? []), [credit])
  const capitalSplit = useMemo(() => splitFigures(capital?.tiles ?? []), [capital])
  const outlookSplit = useMemo(() => splitFigures(outlook?.tiles ?? []), [outlook])

  // T-50: the deals that are on the board but not on the map, as one drill.
  const unmappedDrill = useMemo(() => {
    if (!cityConcentration || cityConcentration.unmappedDrill || !cohortFacts.data) return null
    const mapped = new Set<string>()
    for (const point of cityConcentration.points) {
      const ids = (point.drill?.filters as { ids?: string[] } | undefined)?.ids ?? []
      for (const id of ids) mapped.add(id)
    }
    const ids = unmappedIds(cohortFacts.data.deals, mapped)
    return drillForIds(ids, `Not on the map (${ids.length}) — no city/state, outside the continental US, or no geocode`)
  }, [cityConcentration, cohortFacts.data])

  const scorecardData = useMemo<ScorecardPanelData | null>(() => {
    if (!scorecard) return null
    return {
      overall: scorecard.overall,
      // The server ships each metric as a Figure; the kit's bars want a 0-100
      // number. No target marker is drawn — the payload carries no target.
      metrics: scorecardMetrics(scorecardSplit.known),
      footnote: scorecard.footnote,
    }
  }, [scorecard, scorecardSplit])

  const leaderColumns = useMemo<FigureTableColumn[]>(
    () => [
      { key: 'unit', label: 'Unit', width: 'w-[130px]' },
      { key: 'scope', label: 'Scope', width: 'w-[92px]' },
      { key: 'leader', label: 'Leader', width: 'w-[120px]' },
      { key: 'exposure', label: 'Exposure', align: 'right' },
      { key: 'production', label: 'YTD prod.', align: 'right' },
      { key: 'plan', label: 'Plan %', align: 'right' },
      { key: 'revenue', label: 'Net rev.', align: 'right' },
      { key: 'revenuePlan', label: 'Rev %', align: 'right' },
      { key: 'roe', label: 'ROE', align: 'right' },
      { key: 'watchlist', label: 'Watchlist', align: 'right' },
    ],
    [],
  )

  const leaderTable = useMemo(() => {
    const rows = (units?.rows ?? []).map((row) => ({
      id: String(row.unitId),
      cells: [
        wrap('Unit', row.name, 'text'),
        wrap('Scope', titleCase(row.kind), 'text'),
        wrap('Leader', row.leader, 'text'),
        row.exposure,
        row.production,
        row.productionVsPlan,
        row.netRevenue,
        row.revenueVsPlan,
        row.roePct,
        row.watchlist,
      ],
    }))
    // A column no unit has a value in is a column of dashes: drop it and say
    // so in the footer. The unit name is always kept.
    const keep = leaderColumns.map(
      (_column, index) => index === 0 || rows.some((row) => formatFigure(row.cells[index]) !== '--'),
    )
    return {
      columns: leaderColumns.filter((_column, index) => keep[index]),
      rows: rows.map<FigureTableRow>((row) => ({ ...row, cells: row.cells.filter((_cell, index) => keep[index]) })),
      dropped: leaderColumns.filter((_column, index) => !keep[index]).map((column) => column.label),
    }
  }, [units, leaderColumns])
  const leaderRows = leaderTable.rows

  const alertItems = useMemo<AlertItem[]>(
    () =>
      alertSplit.known.map((alert) => ({
        id: alert.label,
        severity: alertSeverity(alert.tone),
        title: alert.label,
        figure: alert,
        drill: alert.drill,
      })),
    [alertSplit],
  )

  const movementItems = useMemo<MovementItem[]>(
    () =>
      (movement?.rows ?? []).map((row, index) => ({
        // Index always participates: source+timestamp is not unique, and a
        // collision makes React drop one of the rows, hiding a real change.
        id: `${row.source}-${index}-${row.at ?? ''}`,
        occurredAt: row.at ?? '',
        title: row.title,
        detail: [row.change, row.actor].filter(Boolean).join(' · ') || undefined,
        drill: row.drill ?? undefined,
        href: row.href ?? undefined,
      })),
    [movement],
  )

  const quickActionItems = useMemo(
    () =>
      (quickActions?.actions ?? []).map((action) => ({
        id: action.label,
        label: action.label,
        count: action.count,
        drill: action.drill ?? undefined,
      })),
    [quickActions],
  )

  const attentionData = useMemo<AttentionPanelData>(
    () => ({
      items: (attention?.rows ?? []).map((row, index) => ({
        id: `${row.source}-${index}`,
        rank: index + 1,
        title: row.title,
        subtitle: [row.severity ? `${titleCase(row.severity)} risk` : null, row.detail]
          .filter(Boolean)
          .join(' · '),
        figure: wrap('Exposure', row.amount, 'currency', {
          drill: row.drill ?? undefined,
          tone: row.severity ? SEVERITY_TONE[row.severity] : undefined,
        }),
        dueLabel: dueLabel(row.daysToDeadline),
        action: { label: row.action, drill: row.drill ?? undefined, href: row.href ?? undefined },
        tone: row.severity ? SEVERITY_TONE[row.severity] : undefined,
      })),
      footnote: 'Ranked server-side by exposure, urgency and severity.',
    }),
    [attention],
  )

  const concentrationGroups = useMemo(() => {
    const groups = new Map<string, ConcentrationRow[]>()
    for (const row of concentration?.rows ?? []) {
      const bucket = groups.get(row.dimension) ?? []
      bucket.push(row)
      groups.set(row.dimension, bucket)
    }
    return Array.from(groups.entries())
  }, [concentration])

  const breachedLimits = useMemo(
    () =>
      (concentration?.rows ?? [])
        .filter((row) => row.actualPct.tone === 'negative' || row.actualPct.tone === 'warning')
        .map((row) => row.value),
    [concentration],
  )

  const [active, setActive] = useDashboardView(TABS)

  const peopleLink = (
    <Link
      to="/dashboards/people"
      className="flex items-center gap-1 rounded border border-amber-400/25 px-2 py-1 text-[10px] text-amber-200 transition-colors hover:bg-amber-400/10"
    >
      <UserRound size={10} aria-hidden="true" /> People &amp; Team
    </Link>
  )

  function renderActiveGrid() {
    if (active === 'explorer') {
      return <PipelineExplorer showCohortBar={false} />
    }

    if (active === 'geography-team') {
      const coverage = cityConcentration?.coverage
      return (
        <DashboardGrid
          responsive
          className="grid-cols-1 overflow-y-auto xl:grid-rows-[minmax(0,1fr)_minmax(0,1fr)] xl:overflow-hidden"
        >
          <GridCell
            responsive
            panelId="city-concentration"
            panelLabel="Continental-US city concentration"
            className="flex min-h-[32rem] min-w-0 flex-col xl:min-h-[24rem]"
          >
            <Panel
              title="Continental-US city concentration"
              kicker={
                coverage
                  ? `${coverage.mappedDeals.toLocaleString()} of ${coverage.totalDeals.toLocaleString()} deals mapped · ${cohortLabel}`
                  : cohortLabel
              }
              className="flex-1"
              isLoading={isLoading}
              isEmpty={!cityConcentration}
              emptyIcon={Globe}
              emptyTitle="City concentration unavailable"
              emptyDescription="The board response does not contain a city-concentration contract."
              footer={cityConcentration ? mapFootnote(cityConcentration) : undefined}
              bodyClassName="flex min-h-0 flex-col"
            >
              {cityConcentration && <CityConcentrationMap data={cityConcentration} unmappedDrill={unmappedDrill} />}
            </Panel>
          </GridCell>

          <GridCell
            responsive
            panelId="team-workload"
            panelLabel="Team workload"
            className="flex min-h-[20rem] min-w-0 flex-col xl:min-h-0"
          >
            <TeamWorkloadPanel
              cohort={cohort}
              title={`Team workload, staffing & aging${team ? ` · ${team.people.length} people` : ''}`}
              className="flex-1"
              actions={peopleLink}
            />
            {teamRevenue && (
              <p className="mt-1 flex-shrink-0 px-1 text-[9.5px] leading-snug text-stone-500">
                <BasisNote full={(teamRevenue.gates ?? []).map((gate) => `${gate.title}: ${gate.detail}`).join(' ')}>
                  Team revenue is not shown: how revenue counts and how it is split between people still need the client&apos;s definition.
                </BasisNote>
              </p>
            )}
          </GridCell>
        </DashboardGrid>
      )
    }

    if (active === 'production') {
      return (
        <DashboardGrid rows={4}>
          <GridCell panelId="franchise-performance" panelLabel="Global franchise performance" col={2} row={2} colStart={1} rowStart={1}>
            <Panel
              title="Global franchise performance"
              kicker={`Year to date · ${regions.length} regions`}
              className="flex-1"
              isLoading={isLoading}
              isEmpty={regions.length === 0}
              emptyIcon={Globe}
              emptyTitle="No regional units"
              emptyDescription="No business unit of kind 'region' has a closed period in this partition."
              bodyClassName="flex min-h-0 flex-col"
              scroll
              footer="Exposure = committee approved onward; production = funded this year. ROE and plan need a finance close and are not shown until one exists."
            >
              <FranchiseCards units={regions} />
            </Panel>
          </GridCell>

          <GridCell panelId="production-outlook" panelLabel="Production & earnings outlook" col={2} row={2} colStart={3} rowStart={1}>
            <Panel
              title="Production & earnings outlook"
              kicker={
                outlook
                  ? outlookKicker(outlook)
                  : trend?.momProduction?.label
              }
              className="flex-1"
              isLoading={isLoading}
              isEmpty={(trend?.points ?? []).length === 0 && !outlookSplit.known.length}
              emptyIcon={LineChartIcon}
              emptyTitle="No closed periods"
              emptyDescription="Production and revenue appear once a business unit period is closed."
              footer={
                <span className="block space-y-0.5">
                  <UnmeasuredLine figures={outlookSplit.unknown} />
                  {outlook?.basis && <span className="block leading-snug">{outlook.basis}</span>}
                </span>
              }
              bodyClassName="flex min-h-0 flex-col"
            >
              {outlook && <OutlookTiles data={outlook} />}
              <ProductionOutlook points={trend?.points ?? []} />
            </Panel>
          </GridCell>

          <GridCell panelId="leader-scorecard" panelLabel="Regional & business leader scorecard" col={4} row={2} colStart={1} rowStart={3}>
            <Panel
              title="Regional & business leader scorecard"
              kicker={units?.rows.length ? `${units.rows.length} units` : undefined}
              className="flex-1"
              isLoading={isLoading}
              isEmpty={leaderRows.length === 0}
              emptyIcon={Users}
              emptyTitle="No units reporting"
              emptyDescription="Business units appear once they carry a closed period this year."
              scroll
              bodyClassName="!p-0"
              footer={
                leaderTable.dropped.length
                  ? `Not on record for any unit: ${leaderTable.dropped.join(', ')} — plan, revenue and ROE need a finance close; watchlist needs a surveillance tier.`
                  : undefined
              }
            >
              <FigureTable columns={leaderTable.columns} rows={leaderRows} showWorkspaceLink={false} />
            </Panel>
          </GridCell>
        </DashboardGrid>
      )
    }

    if (active === 'risk') {
      const boardMaturities = maturities?.buckets ?? []
      // Deployed, Committed and the deployment ratio already lead the board (headline tiles and the
      // scorecard). The capital panel earns a cell only when a capital plan adds something; otherwise
      // its unknowns join the leverage panel's known-figures line and concentration takes the column.
      const capitalAdds =
        capital?.periodMonth != null ||
        capitalSplit.known.some((tile) => !['Deployed', 'Committed', 'Deployment ratio'].includes(tile.label))
      return (
        <DashboardGrid rows={4}>
          <GridCell
            panelId="exposure-limits"
            panelLabel="Exposure, concentration & limits"
            col={2}
            row={capitalAdds ? 2 : 4}
            colStart={1}
            rowStart={1}
          >
            <Panel
              title="Exposure, concentration & limits"
              kicker={cohortLabel}
              className="flex-1"
              isLoading={isLoading}
              isEmpty={concentrationGroups.length === 0}
              emptyIcon={Layers}
              emptyTitle="No limits configured"
              emptyDescription="Concentration rows appear once policy limits exist for this partition."
              footer={
                [concentrationFooter(concentration?.rows ?? [], breachedLimits), concentration?.basis]
                  .filter(Boolean)
                  .join(' ') || undefined
              }
              scroll
            >
              <div className="space-y-3">
                {concentrationGroups.map(([dimension, rows]) => (
                  <ConcentrationList
                    key={dimension}
                    label={`By ${titleCase(dimension)}`}
                    rows={rows}
                  />
                ))}
              </div>
            </Panel>
          </GridCell>

          <GridCell panelId="portfolio-credit" panelLabel="Portfolio health & credit" col={2} row={2} colStart={3} rowStart={1}>
            <Panel
              title="Leverage & credit"
              kicker={cohortLabel}
              className="flex-1"
              isLoading={isLoading}
              scroll
              bodyClassName="flex min-h-0 flex-col gap-2"
              footer={
                <UnmeasuredLine
                  figures={capitalAdds ? creditSplit.unknown : [...creditSplit.unknown, ...capitalSplit.unknown]}
                  summary={`No source yet: ${(capitalAdds ? creditSplit.unknown : [...creditSplit.unknown, ...capitalSplit.unknown])
                    .map((figure) => figure.label)
                    .join(', ')}.`}
                />
              }
            >
              {creditSplit.known.length > 0 && <StatGrid figures={creditSplit.known} columns={Math.min(3, creditSplit.known.length)} />}
              {(credit?.points ?? []).length > 0 ? (
                <div className="h-[120px] flex-shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={credit?.points ?? []} margin={{ top: 6, right: 4, left: -12, bottom: 0 }}>
                      <CartesianGrid {...chartGridProps()} />
                      <XAxis dataKey="label" {...chartAxisProps(9)} interval="preserveStartEnd" />
                      <YAxis {...chartAxisProps(9)} tickFormatter={amountTick} width={48} />
                      <Tooltip
                        {...CHART_TOOLTIP_PROPS}
                        formatter={(value: number, name: string) => [amountTick(value), titleCase(name)]}
                      />
                      <Line type="monotone" dataKey="watchlist" name="Watchlist" stroke={chartSeriesColor(3)} strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="criticized" name="Criticized" stroke={chartSeriesColor(0)} strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex flex-shrink-0 flex-col">
                  <div className="terminal-kicker mb-1 !text-[8px]">Leverage profile · total commitment and deals by LTV / LTC band</div>
                  <LeverageProfile cohort={cohort} />
                </div>
              )}
            </Panel>
          </GridCell>

          <GridCell
            panelId="maturity-wall"
            panelLabel="Maturity & refinance wall"
            col={2}
            row={2}
            colStart={capitalAdds ? 1 : 3}
            rowStart={3}
          >
            <Panel
              title="Maturity & refinance wall"
              kicker={
                maturities?.fundedLoans != null
                  ? [
                      `${maturities.fundedLoans} funded loans · closing + initial term`,
                      numeric(maturities.pastInitialMaturity)
                        ? `${numeric(maturities.pastInitialMaturity)} past initial maturity${
                            numeric(maturities.pastInsideExtension) != null
                              ? `, ${numeric(maturities.pastInsideExtension)} inside their extension`
                              : ''
                          }`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  : 'Funded loans · closing + initial term'
              }
              className="flex-1"
              isLoading={isLoading}
              footer={
                <span className="block leading-snug">
                  {maturities?.basis ??
                    (hasMaturities(boardMaturities)
                      ? 'Funded loans only — a pipeline request has a term, not a maturity. Bars are balance by maturity window.'
                      : 'Maturity = closing (funded) date + initial term months (settled 2 Nov). Funded volume by year; a request has a term, not a maturity.')}
                </span>
              }
              scroll
              bodyClassName="flex min-h-0 flex-col gap-2"
            >
              {hasMaturities(boardMaturities) ? <MaturityWall buckets={boardMaturities} /> : <MaturityLadder cohort={cohort} />}
              {maturities?.pastInitialMaturity && numeric(maturities.pastInitialMaturity) ? (
                <div className="flex flex-shrink-0 flex-wrap items-baseline gap-x-3 gap-y-1 rounded-md border border-amber-400/20 bg-amber-400/[0.04] px-2.5 py-1.5">
                  <span className="font-mono text-[8.5px] uppercase tracking-[0.16em] text-amber-200/80">Past initial maturity</span>
                  <span className="font-mono text-[13px] font-semibold tabular-nums text-amber-200">
                    <FigureValue figure={maturities.pastInitialMaturity} />
                  </span>
                  {maturities.pastInitialMaturityBalance && (
                    <span className="font-mono text-[11px] tabular-nums text-stone-300">
                      <FigureValue figure={maturities.pastInitialMaturityBalance} />
                    </span>
                  )}
                  {maturities.pastInsideExtension && numeric(maturities.pastInsideExtension) != null && (
                    <span className="flex items-baseline gap-1 text-[10px] text-stone-400">
                      <FigureValue figure={maturities.pastInsideExtension} />
                      <span>inside their extension</span>
                    </span>
                  )}
                  {maturities.pastInitialMaturity.basis && (
                    <BasisNote full={maturities.pastInitialMaturity.basis} className="text-[9.5px] text-stone-500">
                      Servicing would say whether each was extended, repaid or refinanced.
                    </BasisNote>
                  )}
                </div>
              ) : null}
            </Panel>
          </GridCell>

          {capitalAdds && (
          <GridCell panelId="capital-allocation" panelLabel="Capital allocation" col={2} row={2} colStart={3} rowStart={3}>
            <Panel
              title="Capital allocation"
              kicker={capital?.periodMonth ?? undefined}
              className="flex-1"
              isLoading={isLoading}
              isEmpty={capitalSplit.known.length === 0}
              emptyIcon={Landmark}
              emptyTitle="No allocation on record"
              emptyDescription="Capital tiles appear once an allocation month is booked."
              footer={<UnmeasuredLine figures={capitalSplit.unknown} reason="Available capital and reserve need a booked allocation month." />}
              scroll
            >
              <div
                className="grid gap-1.5"
                style={{
                  gridTemplateColumns: `repeat(${Math.min(capitalSplit.known.length || 1, 3)}, minmax(0, 1fr))`,
                }}
              >
                {capitalSplit.known.map((tile) => (
                  <KpiTile key={tile.label} figure={tile} />
                ))}
              </div>
            </Panel>
          </GridCell>
          )}
        </DashboardGrid>
      )
    }

    if (active === 'relationships') {
      const rated = (relationships?.rows ?? []).some((row) => row.health !== 'unrated')
      return (
        <DashboardGrid rows={4}>
          <GridCell panelId="strategic-relationships" panelLabel="Strategic relationships" col={2} row={4} colStart={1} rowStart={1}>
            <Panel
              title="Relationships that feed the book"
              kicker={cohortLabel}
              className="flex-1"
              bodyClassName="!p-0 flex min-h-0 flex-col"
              footer={
                rated
                  ? 'Deal flow by counterparty. Relationship health is rated on some sponsor records; see the sponsor list in CRM.'
                  : 'By deal flow — deals, volume, funded and quote rate — not CRM last contact (T-74). No relationship is health-rated, so none is flagged at risk (T-67).'
              }
            >
              <CounterpartyBoard cohort={cohort} />
            </Panel>
          </GridCell>

          <GridCell panelId="market-signals" panelLabel="Market signals & outlook" col={2} row={2} colStart={3} rowStart={1}>
            <Panel
              title="Market signals & outlook"
              className="flex-1"
              isLoading={isLoading}
              isEmpty={
                (market?.indicators ?? []).length === 0 && (market?.postureNotes ?? []).length === 0
              }
              emptyIcon={LineChartIcon}
              emptyTitle="No market signals"
              emptyDescription="Indicators appear from published market signals, or from parseable coupons and stored market_data on the live loan book."
              scroll
              bodyClassName="flex min-h-0 flex-col gap-2 !px-0 !pb-0"
            >
              <div className="overflow-x-auto scrollbar-thin">
                <table className="dense-table">
                  <thead>
                    <tr>
                      <th className="text-left">Indicator</th>
                      <th className="text-right">Current</th>
                      <th className="text-right">Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(market?.indicators ?? []).map((signal) => (
                      <tr key={signal.indicator}>
                        <td className="truncate text-stone-300" title={signal.commentary ?? undefined}>
                          {signal.indicator}
                          {signal.asOf && <span className="ml-1 font-mono text-[9px] text-stone-600">{signal.asOf}</span>}
                        </td>
                        <td className="text-right font-mono tabular-nums">
                          <FigureValue
                            figure={
                              signal.label
                                ? wrap(signal.indicator, signal.label, 'text')
                                : wrap(signal.indicator, signal.value, 'count')
                            }
                            align="right"
                          />
                        </td>
                        <td className="text-right">
                          <TrendGlyph trend={signal.trend} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="px-3 pb-3">
                <div className="terminal-kicker !text-[8px]">90-day posture</div>
                {(market?.postureNotes ?? []).length === 0 ? (
                  <p className="mt-1 text-[10px] text-stone-500">
                    No posture commentary is published for this period.
                  </p>
                ) : (
                  <ul className="mt-1 space-y-1.5">
                    {(market?.postureNotes ?? []).map((note) => (
                      <li key={note.indicator} className="text-[10.5px] leading-relaxed text-stone-400">
                        <span className="text-stone-200">{note.indicator}</span>
                        {note.commentary ? ` — ${note.commentary}` : ''}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Panel>
          </GridCell>

          <GridCell panelId="global-attention" panelLabel="Global head attention" col={2} row={2} colStart={3} rowStart={3}>
            <Panel
              title={attentionData.items.length ? 'Global head attention' : 'Response & aging watch'}
              kicker={attentionData.items.length ? 'Ranked' : cohortLabel}
              className="flex-1"
              isLoading={isLoading}
              scroll
              footer={
                attentionData.items.length
                  ? undefined
                  : 'No matter, closing, covenant or elevated asset clears the attention threshold, so the desk’s stage-aging proxies stand here instead (T-72/T-73/T-74; thresholds pending sign-off).'
              }
            >
              {attentionData.items.length ? <AttentionTable data={attentionData} /> : <AgingWatch cohort={cohort} />}
            </Panel>
          </GridCell>
        </DashboardGrid>
      )
    }

    // overview (default). The top half is the book itself — where every deal
    // sits and what moved month by month; the bottom half is who carries it,
    // what the scorecard can actually measure, and what just changed.
    return (
      <DashboardGrid rows={4}>
        <GridCell panelId="pipeline-funnel" panelLabel="Pipeline funnel" col={2} row={2}>
          <Panel
            title="Pipeline funnel"
            kicker={cohortLabel}
            className="flex-1"
            isLoading={isLoading}
            isEmpty={!pipelineFunnel}
            emptyIcon={GitBranch}
            emptyTitle="Funnel unavailable"
            emptyDescription="The board response carries no pipeline funnel."
            skeletonRows={8}
            scroll
            bodyClassName="!py-2"
            footer={
              <BasisNote full={pipelineFunnel?.basis}>
                Deals in each stage now; % = share that reached the next stage; dwell in 24-hour days.
              </BasisNote>
            }
          >
            <FunnelStrip data={pipelineFunnel} orientation="column" scopeLabel={cohortLabel} />
          </Panel>
        </GridCell>

        <GridCell panelId="origination-flow" panelLabel="Origination flow" col={2} row={2}>
          <Panel
            title="Origination flow · 24 months"
            kicker={cohortLabel}
            className="flex-1"
            isLoading={isLoading}
            isEmpty={!monthlyFlow}
            emptyIcon={LineChartIcon}
            emptyTitle="Flow unavailable"
            emptyDescription="The board response carries no monthly flow."
            bodyClassName="flex min-h-0 flex-col !pb-1"
          >
            <FlowChart data={monthlyFlow} scopeLabel={cohortLabel} />
          </Panel>
        </GridCell>

        <GridCell panelId="underwriter-leaderboard" panelLabel="Underwriter leaderboard" col={2} row={2}>
          <Leaderboard
            role="underwriter"
            cohort={cohort}
            served={leaderboards?.underwriter?.rows ?? null}
            className="flex-1"
          />
        </GridCell>

        <GridCell panelId="executive-scorecard" panelLabel="Executive scorecard" col={1} row={2}>
          <Panel
            title="Scorecard & health"
            kicker="Firm-wide"
            className="flex-1"
            isLoading={isLoading}
            scroll
            footer={
              <UnmeasuredLine
                figures={[...scorecardSplit.unknown, ...healthSplit.unknown, ...alertSplit.unknown]}
                summary={`${scorecardSplit.unknown.length + healthSplit.unknown.length + alertSplit.unknown.length} scorecard, health and alert figures have no source yet.`}
              />
            }
          >
            <div className="space-y-3">
              {scorecardData && scorecardData.metrics.length > 0 && (
                <ScorecardPanel data={{ ...scorecardData, footnote: undefined }} showDial={scorecardSplit.known.length > 1} dialSize={88} />
              )}
              {healthSplit.known.length > 0 && (
                <div>
                  <div className="terminal-kicker mb-1 !text-[8px]">Enterprise health</div>
                  <RailBars figures={healthSplit.known} />
                </div>
              )}
              {alertItems.length > 0 && (
                <div>
                  <div className="terminal-kicker mb-1 !text-[8px]">Alerts</div>
                  <AlertRail alerts={alertItems} />
                </div>
              )}
              {quickActionItems.some((action) => (action.count ?? 0) > 0) && (
                <QuickActions actions={quickActionItems.filter((action) => (action.count ?? 0) > 0)} />
              )}
            </div>
          </Panel>
        </GridCell>

        <GridCell panelId="recent-movement" panelLabel="Recent movement" col={1} row={2}>
          <Panel
            title="Recent movement"
            className="flex-1"
            isLoading={isLoading}
            isEmpty={movementItems.length === 0}
            emptyIcon={Building2}
            emptyTitle="No status changes"
            emptyDescription="Nothing has moved across asset tiers, market signals or closed unit periods."
            skeletonRows={4}
            scroll
          >
            <MovementRail movements={movementItems} />
          </Panel>
        </GridCell>
      </DashboardGrid>
    )
  }

  const tiles = headlineSplit.known
  const productionVsPlan = scorecardSplit.known.find((metric) => metric.label === 'Production vs plan')

  return (
    // No kicker: the platform header already renders the module label 'CEO',
    // the code 'CEO' and the stage 'Executive Command' one row above.
    <DashboardShell
      dashboardId="ceo"
      dashboardView={active}
      title="Global CRE Command Center"
      subtitle="Enterprise performance, pipeline, risk & capital — every figure opens the deals behind it."
      partition={board.partition}
      dataUpdatedAt={board.dataUpdatedAt}
      isFetching={board.isFetching}
      error={error}
      onRetry={retry}
      filters={peopleLink}
      tabs={
        <DashboardTabs
          tabs={TABS}
          active={active}
          onChange={setActive}
          trailing={
            <span className="hidden items-center gap-1 text-[10px] text-stone-500 xl:flex">
              <GitBranch size={10} aria-hidden="true" />
              Cohort applies to every tab
            </span>
          }
        />
      }
      fill
    >
      <div className="flex h-full min-h-0 flex-col gap-2">
        <CohortBar cohort={cohort} onChange={setCohort} className="flex-shrink-0" />
        {active !== 'explorer' && (
          <>
            <KpiStrip
              tiles={tiles}
              columnsClassName={STRIP_COLUMNS[Math.min(9, Math.max(1, tiles.length))]}
              isLoading={isLoading}
              error={error}
              onRetry={retry}
              className="flex-shrink-0"
            />
            <div className="flex flex-shrink-0 flex-col gap-0.5 px-1">
              <BookUniverseLine universe={universe} />
              <UnmeasuredLine figures={headlineSplit.unknown} lead="Headline not on record" />
            </div>
          </>
        )}
        {active === 'overview' && productionVsPlan && (
          <div className="flex-shrink-0">
            <PlanProgress production={productionVsPlan} revenue={scorecardSplit.known.find((metric) => metric.label === 'Revenue vs plan')} />
          </div>
        )}
        {renderActiveGrid()}
      </div>
    </DashboardShell>
  )
}
